import React, { useEffect, useMemo, useRef, useState } from "react";

function capabilityLabels(reality) {
  const labels = [];
  const c = reality?.capabilities || {};
  if (c.experience?.play) labels.push("Play");
  if (c.experience?.simulate) labels.push("Simulated");
  if (c.experience?.live) labels.push("Live");
  if (c.experience?.viewAR) labels.push("AR");
  else if (c.experience?.view3d) labels.push("3D");
  if (c.transform?.customize) labels.push("Customize");
  if (c.make?.print) labels.push("Print");
  if (c.commerce?.buy) labels.push("Buy");
  else if (c.commerce?.license) labels.push("License");
  else if (c.commerce?.enquire) labels.push("Custom");
  return labels.slice(0, 3);
}

function groundingLabel(reality) {
  const grounding = reality?.grounding;
  if (!grounding) return null;
  if (grounding.realityIndex == null) {
    return { index: "—", label: grounding.label || "Unverified", className: "unverified" };
  }
  return {
    index: grounding.realityIndex,
    label: grounding.label || grounding.classification || "Reality",
    className: grounding.classification || "unverified",
  };
}

export default function RealityCard({ reality, featured = false, compact = false }) {
  const videoRef = useRef(null);
  const [previewing, setPreviewing] = useState(false);
  const [motionAllowed, setMotionAllowed] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setMotionAllowed(!query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  const title = reality?.identity?.title || "Untitled Reality";
  const category = reality?.identity?.categoryLabel || "Reality";
  const brand = reality?.identity?.brand || null;
  const cover = reality?.cover?.url || reality?.media?.poster?.url || null;
  const motion = reality?.representations?.motionPreview || reality?.media?.motion?.url || null;
  const labels = capabilityLabels(reality);
  const grounding = groundingLabel(reality);
  const href = `/r/${encodeURIComponent(reality.slug)}`;

  const proofText = useMemo(() => {
    if (!reality?.grounding) return null;
    const count = Number(reality.grounding.sourceCount || 0);
    if (count > 0) return `${count} independent source${count === 1 ? "" : "s"}`;
    if (reality.grounding.realityIndex == null) return "Proof not established";
    return "Grounding assessed";
  }, [reality]);

  const startPreview = () => {
    if (!motion || !motionAllowed) return;
    setPreviewing(true);
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  };

  const stopPreview = () => {
    setPreviewing(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      try { video.currentTime = 0; } catch {}
    }
  };

  return (
    <a
      className={`reality-card${featured ? " reality-card--featured" : ""}${compact ? " reality-card--compact" : ""}`}
      href={href}
      aria-label={`Experience ${title}`}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onFocus={startPreview}
      onBlur={stopPreview}
    >
      <div className={`reality-card__media${cover ? " has-cover" : " no-cover"}`}>
        {cover ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            decoding="async"
            onError={event => {
              event.currentTarget.style.display = "none";
              event.currentTarget.parentElement?.classList.add("media-failed");
            }}
          />
        ) : null}

        {motion ? (
          <video
            ref={videoRef}
            className={`reality-card__motion${previewing ? " is-previewing" : ""}`}
            src={motion}
            muted
            playsInline
            loop
            preload="metadata"
            tabIndex={-1}
            aria-hidden="true"
          />
        ) : null}

        <div className="reality-card__fallback" aria-hidden="true">
          <span>DoAR</span>
          <small>Design Object for Augmenting Reality</small>
        </div>

        <div className="reality-card__media-badges" aria-hidden="true">
          {reality?.live?.active ? <span className="reality-card__live">LIVE</span> : null}
          {reality?.capabilities?.experience?.play ? <span className="reality-card__play">▶</span> : null}
          {reality?.capabilities?.experience?.viewAR ? <span className="reality-card__ar">AR</span> : null}
        </div>

        {grounding ? (
          <span className={`reality-card__ri reality-card__ri--${grounding.className}`}>
            <b>RI {grounding.index}</b>
            <em>{grounding.label}</em>
          </span>
        ) : null}
      </div>

      <div className="reality-card__body">
        <div className="reality-card__eyebrow-row">
          <div className="reality-card__eyebrow">{category}</div>
          {reality?.media?.state === "preparing" ? <span className="reality-card__state">Preparing</span> : null}
        </div>

        <h3>{title}</h3>
        {brand ? <p className="reality-card__brand">{brand}</p> : null}

        {proofText ? (
          <p className="reality-card__proof">
            <span aria-hidden="true">◇</span>
            {proofText}
          </p>
        ) : null}

        <div className="reality-card__meta">
          {labels.length
            ? labels.map(label => <span key={label}>{label}</span>)
            : <span>View Reality</span>}
          {reality?.commerce?.priceLabel ? <b>{reality.commerce.priceLabel}</b> : null}
        </div>
      </div>
    </a>
  );
}

export { capabilityLabels, groundingLabel };
